import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Loader2, FileText, FolderOpen, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document categories
const DOCUMENT_CATEGORIES = [
  { value: 'pleadings', label: 'Pleadings' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'medical', label: 'Medical Records' },
  { value: 'financial', label: 'Financial' },
  { value: 'court_orders', label: 'Court Orders' },
  { value: 'client_docs', label: 'Client Documents' },
  { value: 'internal', label: 'Internal' },
  { value: 'other', label: 'Other' }
];

export default function DocumentEditDialog({ 
  open, 
  onOpenChange, 
  document,
  caseId,
  folders = [],
  onSave 
}) {
  const { token } = useAuth();
  
  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('other');
  const [folderId, setFolderId] = useState('__auto__');  // Use special value for "auto"
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialize form when document changes
  useEffect(() => {
    if (document) {
      setName(document.original_filename || '');
      setCategory(document.category || 'other');
      setFolderId(document.folder_id || '__auto__');  // Use special value when no folder
      setDescription(document.description || '');
    }
  }, [document]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: name.trim(),
        category,
        description: description.trim() || null
      };

      // Only include folder_id if explicitly selected (not "auto")
      if (folderId && folderId !== '__auto__') {
        updateData.folder_id = folderId;
      }

      const res = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/documents/${document.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (res.ok) {
        const updatedDoc = await res.json();
        toast.success('Document updated successfully');
        onOpenChange(false);
        if (onSave) onSave(updatedDoc);
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update document');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Edit Document
          </DialogTitle>
          <DialogDescription>
            Update document properties, category, and folder location.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Document Name */}
          <div className="space-y-2">
            <Label htmlFor="doc-name">Document Name</Label>
            <Input
              id="doc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter document name"
              data-testid="doc-name-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Category
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Changing category will automatically move the document to the matching folder.
            </p>
          </div>

          {/* Folder */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              Folder
            </Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger data-testid="folder-select">
                <SelectValue placeholder="Select folder (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto (based on category)</SelectItem>
                {folders.map(folder => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Override automatic folder assignment, or leave as "Auto" to use category-based placement.
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="doc-description">Description (optional)</Label>
            <Textarea
              id="doc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes or description..."
              rows={3}
              data-testid="doc-description-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-doc-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
